import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, forkJoin, of } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import {
  CreateDeviceDowntimeRequest,
  DeviceDowntimeImpactPreview,
  DeviceDowntimeItem
} from '../data-access/downtime.models';
import { DowntimeService } from '../data-access/downtime.service';
import { DowntimeClinicOption, DowntimeFormComponent } from '../components/downtime-form.component';
import { DowntimeImpactSummaryComponent } from '../components/downtime-impact-summary.component';
import { DowntimeQuickViewModalComponent } from '../components/downtime-quick-view-modal.component';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

@Component({
  selector: 'app-downtime-create-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    DowntimeFormComponent,
    DowntimeImpactSummaryComponent,
    DowntimeQuickViewModalComponent,
    TranslatePipe
  ],
  template: `
    <app-page-header
      title="Create Device Downtime"
      subtitle="Manual maintenance and outage registration"
    />

    @if (createdDowntime) {
      <app-downtime-quick-view-modal
        [isOpen]="true"
        [item]="createdDowntime"
        [clinics]="clinicOptions"
        [devices]="deviceOptions"
        (close)="closeCreatedDowntime()"
      />
    }

    @if (!createdDowntime && previewImpact && pendingCreatePayload) {
      <app-downtime-impact-summary
        [affectedAppointmentIds]="previewImpact.affectedAppointmentIds"
        [reachabilityImpact]="previewImpact.reachabilityImpact"
      />

      <div class="downtime-create-page__preview-actions">
        <button
          type="button"
          class="downtime-create-page__secondary"
          [disabled]="isSubmitting"
          (click)="backToForm()"
        >
          {{ 'Back to edit' | t }}
        </button>
        <button
          type="button"
          class="downtime-create-page__primary"
          [disabled]="isSubmitting"
          (click)="confirmCreateDowntime()"
        >
          {{ (isSubmitting ? 'Creating...' : 'Create downtime') | t }}
        </button>
      </div>
    } @else if (!createdDowntime) {
      <app-downtime-form
        [isSubmitting]="isSubmitting"
        [errorText]="errorText"
        [clinics]="clinicOptions"
        [devices]="deviceOptions"
        [initialValue]="pendingCreatePayload"
        (submitted)="onSubmitted($event)"
      />
    }
  `,
  styles: [`
    .downtime-create-page__preview-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin: 16px 0;
    }

    .downtime-create-page__secondary,
    .downtime-create-page__primary {
      min-height: 40px;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .downtime-create-page__secondary {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
    }

    .downtime-create-page__primary {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 640px) {
      .downtime-create-page__preview-actions {
        flex-direction: column;
      }

      .downtime-create-page__secondary,
      .downtime-create-page__primary {
        width: 100%;
      }
    }
  `]
})
export class DowntimeCreatePageComponent
{
  private readonly downtimeService = inject(DowntimeService);
  private readonly settingsService = inject(SettingsService);
  private readonly devicesService = inject(DevicesService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  protected isSubmitting = false;
  protected errorText: string | null = null;
  protected createdDowntime: DeviceDowntimeItem | null = null;
  protected previewImpact: DeviceDowntimeImpactPreview | null = null;
  protected pendingCreatePayload: CreateDeviceDowntimeRequest | null = null;
  protected clinicOptions: DowntimeClinicOption[] = [];
  protected deviceOptions: Array<{ id: string; label: string; clinicId: string }> = [];

  public constructor()
  {
    this.loadReferenceOptions();
  }

  protected onSubmitted(payload: CreateDeviceDowntimeRequest): void
  {
    if (this.isSubmitting || this.createdDowntime) {
      return;
    }
    this.isSubmitting = true;
    this.errorText = null;
    this.createdDowntime = null;
    this.previewImpact = null;
    this.pendingCreatePayload = null;

    this.downtimeService.previewImpact(payload).subscribe({
      next: (preview: DeviceDowntimeImpactPreview) =>
      {
        this.previewImpact = preview;
        this.pendingCreatePayload = payload;
        this.isSubmitting = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to preview downtime impact', error);
        this.errorText = this.resolveCreateErrorText(
          error,
          'Backend rejected device downtime impact preview request.',
        );
        this.isSubmitting = false;
      }
    });
  }

  protected backToForm(): void
  {
    if (this.isSubmitting) {
      return;
    }

    this.previewImpact = null;
    this.errorText = null;
  }

  protected confirmCreateDowntime(): void
  {
    if (this.isSubmitting || this.createdDowntime || !this.pendingCreatePayload) {
      return;
    }

    this.createDowntime(this.pendingCreatePayload);
  }

  private createDowntime(payload: CreateDeviceDowntimeRequest): void
  {
    this.isSubmitting = true;
    this.errorText = null;
    this.createdDowntime = null;

    this.downtimeService.create(payload).subscribe({
      next: (downtime: DeviceDowntimeItem) =>
      {
        this.createdDowntime = downtime;
        this.previewImpact = null;
        this.pendingCreatePayload = null;
        this.isSubmitting = false;

        this.downtimeService.getById(downtime.id).subscribe({
          next: (detail) => {
            if (this.createdDowntime?.id === detail.id) {
              this.createdDowntime = detail;
            }
          },
          error: (error: unknown) => console.error('Failed to load created device downtime detail', error),
        });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to create downtime', error);
        this.errorText = this.resolveCreateErrorText(
          error,
          'Backend rejected device downtime creation request.',
        );
        this.isSubmitting = false;
      }
    });
  }

  protected closeCreatedDowntime(): void
  {
    void this.router.navigate(this.withTenantPath(['downtime', 'device']));
  }

  private resolveCreateErrorText(error: unknown, fallback: string): string
  {
    return resolveHttpErrorMessageKey(error, {
      forbidden: 'error.forbiddenDowntimeCreate',
      badRequestByMessage: {
        'startTime must align to 15-minute slot grid': 'error.downtimeStartSlotGrid',
        'expectedRepairUntil must align to 15-minute slot grid': 'error.downtimeEndSlotGrid',
        'expectedRepairUntil must be greater than startTime': 'error.downtimeEndAfterStart',
        'Invalid downtime time range': 'error.downtimeInvalidTimeRange',
      },
      fallback
    });
  }

  private withTenantPath(segments: Array<string>): Array<string>
  {
    const role = this.authService.getCurrentRole();
    const slug = this.authService.getPreferredCompanySlug();

    if (role !== 'system_admin' && slug)
    {
      return ['/', slug, ...segments];
    }

    return ['/', ...segments];
  }

  private loadReferenceOptions(): void
  {
    forkJoin({
      clinics: this.settingsService.getClinicOperationalSettings().pipe(
        catchError(() => of([] as ClinicOperationalSettingsDto[])),
      ),
      devices: this.devicesService.getList().pipe(
        catchError(() => of([] as DeviceItem[])),
      ),
    }).subscribe({
      next: ({ clinics, devices }) =>
      {
        this.clinicOptions = clinics.map((item) => ({
          id: item.clinicId,
          label: item.clinicName,
          workingDayStart: item.workingDayStart,
          workingDayEnd: item.workingDayEnd,
          slotDurationMinutes: item.effectiveSlotDurationMinutes,
        }));

        this.deviceOptions = devices
          .filter((item) => item.status !== 'retired')
          .map((item) => ({
            id: item.id,
            clinicId: item.clinicId,
            label: [item.brand, item.model, item.serialNumber]
              .map((part) => part?.trim())
              .filter((part): part is string => !!part)
              .join(' / ') || item.id,
          }));
      },
    });
  }
}
